import { UserType } from '../lib/auth/types';

/**
 * Metric metadata: unit, conversion factor, and baseline thresholds
 */
export const metricInfo: { [key: string]: { unit: string; conversion: number } } = {
    lcp: { unit: 's', conversion: 1000 },
    cls: { unit: '', conversion: 1 },
    tti: { unit: 's', conversion: 1000 },
    fcp: { unit: 's', conversion: 1000 },
    ttfb: { unit: 's', conversion: 1000 },
    tbt: { unit: 's', conversion: 1000 },
};

/**
 * Returns the list of metrics allowed for a given user type/plan
 * @param userType - The user's plan type (from auth context or monitoring usage)
 * @returns Array of allowed metric keys
 */
export function getMetricsForPlan(userType: string | UserType | undefined): string[] {
    if (!userType) return [];
    
    const normalizedType = String(userType).toLowerCase();
    
    switch (normalizedType) {
        case 'enterprise':
            return ['lcp', 'cls', 'tti', 'fcp', 'ttfb', 'tbt'];
        case 'pro':
            return ['lcp', 'cls', 'tti'];
        case 'basic':
            return ['lcp', 'cls'];
        default:
            return [];
    }
}

/**
 * Gets the user's plan type from either:
 * 1. user.user_type from auth context (primary - source of truth)
 * 2. monitoringUsage.accessContext.planFeatures.user_type_granted (fallback)
 * 
 * @param planFeaturesFromUsage - The planFeatures object from monitoring usage endpoint
 * @param userTypeFromAuth - The user type from auth context
 * @returns The determined user type string
 */
export function getUserPlanType(
    planFeaturesFromUsage: any | undefined,
    userTypeFromAuth: UserType | undefined
): string | undefined {
    // PRIORITY: Auth context user type is the source of truth (from Firebase custom claims)
    if (userTypeFromAuth) {
        return String(userTypeFromAuth);
    }
    // Fallback to plan features from API if auth context doesn't have user type
    if (planFeaturesFromUsage?.user_type_granted) {
        return String(planFeaturesFromUsage.user_type_granted);
    }
    return undefined;
}

/**
 * Calculate range for IDEAL baseline: Always ideal ±15%
 * Simple, predictable range based on ideal scores only
 * 
 * @param idealScore - The ideal threshold score (in milliseconds/raw units)
 * @param conversionFactor - Metric conversion factor (1000 for seconds, 1 for milliseconds)
 * @returns Object with min and max range values (in display units)
 */
export function calculateIdealScoreRange(
    idealScore: number,
    conversionFactor: number = 1
): { min: number; max: number } {
    const displayIdeal = idealScore / conversionFactor;
    return {
        min: Math.max(0, displayIdeal * 0.85),
        max: displayIdeal * 1.15,
    };
}

/**
 * Calculate range for CURRENT baseline: Always current ±15%
 * Range based on actual performance from latest scan
 * 
 * Special handling:
 * - If current score is 0 (cannot calculate meaningful range), fall back to ideal range
 * - Min is always >= 0 (no negative scores allowed)
 * 
 * @param currentScore - The latest score from scan (in milliseconds/raw units)
 * @param idealScore - The ideal threshold score (fallback if no current score)
 * @param conversionFactor - Metric conversion factor (1000 for seconds, 1 for milliseconds)
 * @param metric - The metric name (for reference)
 * @returns Object with min and max range values (in display units)
 */
export function calculateCurrentScoreRange(
    currentScore: number | null,
    idealScore: number,
    conversionFactor: number = 1,
    metric?: string
): { min: number; max: number } {
    // If no current score available, fall back to ideal range
    if (currentScore === null || currentScore === undefined) {
        return calculateIdealScoreRange(idealScore, conversionFactor);
    }
    
    const displayCurrent = currentScore / conversionFactor;
    
    // If current score is 0, we cannot calculate ±15% (would be 0,0)
    // Fall back to ideal range to provide a usable range
    if (displayCurrent === 0) {
        return calculateIdealScoreRange(idealScore, conversionFactor);
    }
    
    // For all metrics: current ±15%
    // Ensure min is never negative
    return {
        min: Math.max(0, displayCurrent * 0.85),
        max: displayCurrent * 1.15,
    };
}
